/**
 * Detects iPad Shift+Tab column skips so the table can correct focus.
 *
 * On iPad with an external keyboard, UIKit moves focus before React Native's
 * onKeyPress fires, so an off-screen column can get jumped over. We snapshot
 * the blurring cell, and on the next focus check the gap is within
 * SHIFT_TAB_DEBOUNCE_MS, the direction is backward, and the new cell isn't
 * the expected adjacent one — then redirect to it.
 *
 */
import { RefObject } from 'react';

export interface BlurredCell {
  columnId: string;
  rowIndex: number;
  timestamp: number;
}

export type BlurredCellRef = RefObject<BlurredCell | null>;

// 100ms debounce window — above the observed ~1-5ms iPad blur→focus
// gap, but small enough to filter unrelated focus changes.
const SHIFT_TAB_DEBOUNCE_MS = 100;

/**
 * Creates a time-based debounce gate. Each `trigger()` (re)opens the window;
 * `isActive()` reports whether the most recent trigger is still within
 * `debounceMs`. Subsequent triggers extend the window from the latest call.
 *
 * @param debounceMs - How long after `trigger()` the gate stays active.
 * @returns An object with `trigger()` and `isActive()`.
 */
const createDebounceEvent = (debounceMs: number) => {
  let triggeredAt = 0;
  return {
    isActive: () => Date.now() - triggeredAt < debounceMs,
    trigger: () => {
      triggeredAt = Date.now();
    },
  };
};

/**
 * Triggered when a cell handles Shift+Tab; checked by the search input so it
 * can bounce focus back to the table when UIKit's backward tab-traversal
 * lands there instead of the previous cell.
 */
export const shiftTabDebounce = createDebounceEvent(SHIFT_TAB_DEBOUNCE_MS);

export enum CellDirection {
  Backward = 'backward',
  Forward = 'forward',
}

/** Atomically read and clear the ref — the record is single-use. */
export const consumeBlurredCell = (
  ref: BlurredCellRef | undefined,
): BlurredCell | null => {
  if (!ref) {
    return null;
  }
  const value = ref.current;
  ref.current = null;
  return value;
};

export const recordBlurredCell = (
  ref: BlurredCellRef | undefined,
  cell: Omit<BlurredCell, 'timestamp'>,
) => {
  if (!ref) {
    return;
  }
  ref.current = { ...cell, timestamp: Date.now() };
};

/** Returns the next cell in the given direction, wrapping at row boundaries. */
export const computeAdjacentCell = (
  row: number,
  colIndex: number,
  columnCount: number,
  rowCount: number,
  direction: CellDirection,
): { colIndex: number; row: number } | null => {
  switch (direction) {
    case CellDirection.Backward:
      if (colIndex > 0) {
        return { colIndex: colIndex - 1, row };
      }
      return row > 0 ? { colIndex: columnCount - 1, row: row - 1 } : null;

    case CellDirection.Forward:
      if (colIndex < columnCount - 1) {
        return { colIndex: colIndex + 1, row };
      }
      return row < rowCount - 1 ? { colIndex: 0, row: row + 1 } : null;
  }
};

/**
 * Detects if iOS skipped a cell during focus navigation and returns the
 * correct target, or null if no redirect is needed.
 */
export const detectFocusSkip = ({
  blurred,
  editable,
  newColumnId,
  newRowIndex,
  rowCount,
}: {
  blurred: BlurredCell | null;
  editable: string[];
  newColumnId: string;
  newRowIndex: number;
  rowCount: number;
}): { columnId: string; rowIndex: number } | null => {
  if (!blurred) {
    return null;
  }
  if (Date.now() - blurred.timestamp >= SHIFT_TAB_DEBOUNCE_MS) {
    return null;
  }

  const prevColIndex = editable.indexOf(blurred.columnId);
  const newColIndex = editable.indexOf(newColumnId);
  if (prevColIndex === -1 || newColIndex === -1) {
    return null;
  }

  // Short circuits if we are clicking on a different row to prevent weird behavior
  // when going backwards
  if (blurred.rowIndex !== newRowIndex) {
    return null;
  }

  const isBackward = newColIndex < prevColIndex;
  if (!isBackward) {
    return null;
  }

  const expected = computeAdjacentCell(
    blurred.rowIndex,
    prevColIndex,
    editable.length,
    rowCount,
    CellDirection.Backward,
  );
  if (!expected) {
    return null;
  }

  if (expected.row === newRowIndex && expected.colIndex === newColIndex) {
    return null;
  }

  const targetColumnId = editable[expected.colIndex];
  if (targetColumnId === undefined) {
    return null;
  }
  return { columnId: targetColumnId, rowIndex: expected.row };
};
